import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import usePageMeta from "@/lib/usePageMeta";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, Phone, Calendar, ShieldCheck, Clock, Users } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { trackConversion, trackEvent } from "@/components/AnalyticsTracker";

const CANTONS = ["VD", "GE", "NE", "FR", "VS", "JU", "BE", "ZH", "SG", "TI", "AG", "BL", "BS", "LU", "SO", "TG", "ZG", "SZ", "GL", "GR", "OW", "NW", "UR", "AI", "AR", "SH"];
const COVERS = ["<30", "30–60", "60–120", ">120"];

export default function Reserver() {
  const { t, i18n } = useTranslation();
  usePageMeta("book");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    restaurant_name: "",
    canton: "",
    covers_per_day: "",
    preferred_slot: "",
    message: "",
    source: "landing",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, lang: (i18n.language || "fr").slice(0, 2) });
      trackEvent("call_booked", { source: form.source || "reserver" });
      trackConversion("lead", 0);
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur — réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg">
      <header className="max-w-4xl mx-auto px-6 pt-6 pb-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-primary-c flex items-center justify-center text-white font-display text-lg">O</div>
          <span className="font-display text-xl">Oresto</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link to="/" className="text-sm text-text-secondary hover:text-[var(--text)] flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> {t("reserver.back")}
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6 page-enter">
        {sent ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card-c max-w-xl mx-auto text-center py-12 border-2 border-primary-c"
            data-testid="reserver-success"
          >
            <CheckCircle2 className="w-16 h-16 text-primary-c mx-auto" strokeWidth={1.5} />
            <h2 className="font-display text-3xl mt-4">{t("reserver.success_title")}</h2>
            <p className="text-text-secondary mt-3 max-w-md mx-auto">
              {t("reserver.success_msg", { name: form.first_name })}
            </p>
            <p className="mt-2 text-sm text-primary-c font-semibold">📞 {t("reserver.success_soon")}</p>
            <Link to="/" className="btn-primary inline-block mt-8">{t("reserver.back_home")}</Link>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-6 lg:gap-10 items-start">
            {/* Left: benefits & trust */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-secondary-c bg-secondary-c/10 px-3 py-1 rounded-full">
                <Phone className="w-3.5 h-3.5" /> {t("reserver.badge")}
              </span>
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl mt-4 leading-tight">
                {t("reserver.title_1")} <span className="font-serif-italic text-primary-c">{t("reserver.title_accent")}</span> {t("reserver.title_2")}
              </h1>
              <p className="mt-4 text-text-secondary text-base leading-relaxed">
                {t("reserver.subtitle")}
              </p>

              <ul className="mt-8 space-y-4">
                {[
                  { icon: Clock, k: "reserver.b1" },
                  { icon: Users, k: "reserver.b2" },
                  { icon: ShieldCheck, k: "reserver.b3" },
                ].map(({ icon: Icon, k }, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-primary-c/10 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-primary-c" strokeWidth={1.7} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{t(`${k}_title`)}</p>
                      <p className="text-sm text-text-secondary mt-0.5">{t(`${k}_desc`)}</p>
                    </div>
                  </li>
                ))}
              </ul>

            </motion.div>

            {/* Right: form */}
            <motion.form
              onSubmit={onSubmit}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="card-c shadow-xl"
              data-testid="reserver-form"
            >
              <div className="flex items-center gap-2 mb-5">
                <Calendar className="w-5 h-5 text-primary-c" />
                <h2 className="font-display text-2xl">{t("reserver.form_title")}</h2>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-text-secondary block mb-2">{t("reserver.first_name")} *</label>
                  <input required minLength={1} value={form.first_name} onChange={upd("first_name")} className="input-c" data-testid="reserver-first-name" placeholder="Marc" />
                </div>
                <div>
                  <label className="text-sm font-medium text-text-secondary block mb-2">{t("reserver.last_name")} *</label>
                  <input required minLength={1} value={form.last_name} onChange={upd("last_name")} className="input-c" data-testid="reserver-last-name" placeholder="Dupont" />
                </div>
              </div>

              <div className="mt-3">
                <label className="text-sm font-medium text-text-secondary block mb-2">{t("reserver.email")} *</label>
                <input required type="email" value={form.email} onChange={upd("email")} className="input-c" data-testid="reserver-email" placeholder="vous@restaurant.ch" />
              </div>

              <div className="mt-3">
                <label className="text-sm font-medium text-text-secondary block mb-2">{t("reserver.phone")} *</label>
                <input required type="tel" minLength={6} value={form.phone} onChange={upd("phone")} className="input-c" data-testid="reserver-phone" placeholder="+41 79 123 45 67" />
              </div>

              <div className="mt-3">
                <label className="text-sm font-medium text-text-secondary block mb-2">{t("reserver.restaurant")}</label>
                <input value={form.restaurant_name} onChange={upd("restaurant_name")} className="input-c" data-testid="reserver-restaurant" placeholder="Brasserie du Lac" />
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-sm font-medium text-text-secondary block mb-2">{t("reserver.canton")}</label>
                  <select value={form.canton} onChange={upd("canton")} className="input-c" data-testid="reserver-canton">
                    <option value="">{t("reserver.select_option")}</option>
                    {CANTONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-secondary block mb-2">{t("reserver.covers")}</label>
                  <select value={form.covers_per_day} onChange={upd("covers_per_day")} className="input-c" data-testid="reserver-covers">
                    <option value="">{t("reserver.select_option")}</option>
                    {COVERS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-3">
                <label className="text-sm font-medium text-text-secondary block mb-2">{t("reserver.slot")}</label>
                <input value={form.preferred_slot} onChange={upd("preferred_slot")} className="input-c" data-testid="reserver-slot" placeholder={t("reserver.slot_placeholder")} />
              </div>

              <div className="mt-3">
                <label className="text-sm font-medium text-text-secondary block mb-2">{t("reserver.message_label")}</label>
                <textarea value={form.message} onChange={upd("message")} rows={3} className="input-c" data-testid="reserver-message" style={{ resize: "vertical" }} placeholder={t("reserver.message_placeholder")} />
              </div>

              <button disabled={loading} className="btn-primary w-full mt-5" data-testid="reserver-submit">
                {loading ? t("reserver.sending") : t("reserver.submit")}
              </button>

              <p className="text-[11px] text-text-secondary text-center mt-3 leading-relaxed">
                🔒 {t("reserver.privacy_note")}
              </p>
            </motion.form>
          </div>
        )}
      </main>
    </div>
  );
}
